import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, CheckCircle, TrendingUp } from "lucide-react";
import axios from "axios";
import emailjs from "@emailjs/browser";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// EmailJS config
const EMAILJS_SERVICE_ID = "service_kvot05p";
const EMAILJS_TEMPLATE_ID = "template_r3x21la";
const EMAILJS_PUBLIC_KEY = "zZatKajcIyMeuEezi";

// Send notification email to admin
const sendNotificationEmail = async (email, phone, source) => {
  try {
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      {
        subscriber_email: email,
        subscriber_phone: phone,
        source: source,
        signup_time: new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
      },
      EMAILJS_PUBLIC_KEY
    );
    console.log("Notification email sent successfully");
  } catch (error) {
    console.error("Failed to send notification email:", error);
  }
};

const SignupModal = ({ open, onOpenChange, source = "website" }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    smsConsent: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.phone) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/subscribers`, {
        email: formData.email,
        phone: formData.phone,
        sms_consent: formData.smsConsent,
        source: source,
      });
      setSuccess(true);
      toast.success("Welcome to Compoundr!");
      // Send notification email to admin
      sendNotificationEmail(formData.email, formData.phone, source);
      setTimeout(() => {
        setSuccess(false);
        setFormData({ email: "", phone: "", smsConsent: false });
        onOpenChange(false);
      }, 2000);
    } catch (error) {
      if (error.response?.data?.detail === "Email already subscribed") {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="signup-modal">
        {success ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
              You're In!
            </h3>
            <p className="text-slate-600">
              Check your inbox for a welcome message.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
              </div>
              <DialogTitle className="font-heading text-xl">
                Start Your Investing Journey
              </DialogTitle>
              <DialogDescription>
                Get free weekly investing basics + seminar invites. No spam, just value.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@university.ca"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  data-testid="signup-email-input"
                  className="focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  data-testid="signup-phone-input"
                  className="focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="smsConsent"
                  checked={formData.smsConsent}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, smsConsent: checked })
                  }
                  data-testid="signup-sms-consent"
                />
                <Label htmlFor="smsConsent" className="text-xs text-slate-500 leading-relaxed">
                  I agree to receive SMS updates about free seminars and resources. 
                  Msg & data rates may apply. Reply STOP to unsubscribe.
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white btn-hover-scale"
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  "Join Compoundr"
                )}
              </Button>

              <p className="text-xs text-center text-slate-400">
                By signing up, you agree to our{" "}
                <a href="/privacy" className="text-emerald-600 hover:underline">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/terms" className="text-emerald-600 hover:underline">
                  Terms
                </a>
                .
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
